import * as R from "remeda";
import type {
  AutomationCandidate,
  ActionCandidate,
  InputCandidate,
} from "../../../types/candidate";
import type { Doc } from "@smartsuite/smartdoc";
import type {
  ChecklistConfig,
  ActionReference,
} from "@smartsuite/automation-engine-api";
import { compareActionReferences } from "../../../utils/conditions-coverter.hook";
import { CURRENT_ITEM_OUTPUT_ID } from "../../../utils/constants";
import { OperatorDescriptionKinds } from "../../../containers/AutomationForm/Forms/InputForm/FieldEditor/FieldEditorRow/containers/UpdateValueDropdown/constanst";
import { mapActionsDeep } from "./action-group-walk";

const filterNonEmptyInputs = (inputs: InputCandidate[]): InputCandidate[] =>
  R.filter(
    inputs,
    (input) =>
      input.value?.values.length !== 0 ||
      input.operatorConfig?.operator.oneofKind ===
        OperatorDescriptionKinds.CLEAR,
  );

const processAction = (action: ActionCandidate): ActionCandidate => ({
  ...action,
  inputs: filterNonEmptyInputs(action.inputs),
});

export const ejectEmptyInputs = (
  automation: AutomationCandidate,
): AutomationCandidate => ({
  ...automation,
  actionGroups: mapActionsDeep(automation.actionGroups, processAction),
});

export const ejectReferences = (
  action: ActionCandidate,
  reference: string = "triggerReference",
  whatActionReferencesRemove?: ActionReference,
  outputIdToRemove?: string,
): ActionCandidate => ({
  ...action,
  inputs: R.map(action.inputs, (input) => {
    if (input.value?.values.length) {
      input.value.values = input.value.values.filter((value) => {
        if (whatActionReferencesRemove) {
          if (
            value.value.oneofKind === "reference" &&
            value.value.reference.source.oneofKind === "actionReference" &&
            shouldRemoveActionReference(
              value.value.reference.source.actionReference,
              whatActionReferencesRemove,
              value.value.reference.outputId,
              outputIdToRemove,
            )
          ) {
            return false;
          }
          return true;
        }
        return (
          value.value.oneofKind !== "reference" ||
          value.value.reference.source.oneofKind !== reference
        );
      });

      // Remove references(pills) from smartdoc
      input.value.values = R.map(input.value.values, (value) => {
        if (value.value.oneofKind !== "smartdoc") {
          return value;
        }
        const smartdocValue: Doc = JSON.parse(value.value.smartdoc);
        return {
          value: {
            ...value.value,
            smartdoc: JSON.stringify({
              ...smartdocValue,
              content: smartdocValue.content.map((contentItem) => ({
                ...contentItem,
                content: contentItem.content?.filter((item) => {
                  if (item.type === "pill") {
                    if (whatActionReferencesRemove && item.attrs?.value) {
                      const parsedValue = JSON.parse(
                        item.attrs.value as string,
                      );
                      if (
                        parsedValue.source?.oneofKind === "actionReference" &&
                        shouldRemoveActionReference(
                          parsedValue.source.actionReference,
                          whatActionReferencesRemove,
                          parsedValue.outputId ?? parsedValue.value,
                          outputIdToRemove,
                        )
                      ) {
                        return false;
                      }
                      return true;
                    } else {
                      return false;
                    }
                  }
                  return false;
                }),
              })),
            }),
          },
        };
      });

      // Remove references(pills) from checklist
      input.value.values = R.map(input.value.values, (value) => {
        if (value.value.oneofKind !== "checklist") {
          return value;
        }
        const checklistValue: ChecklistConfig = value.value.checklist;

        return {
          value: {
            ...value.value,
            checklist: {
              ...checklistValue,
              itemsToAdd: checklistValue.itemsToAdd.map((item) => {
                if (item.member !== undefined) {
                  item.member.values = item.member.values.filter((value) => {
                    if (whatActionReferencesRemove) {
                      if (
                        value.value.oneofKind === "reference" &&
                        value.value.reference.source.oneofKind ===
                          "actionReference" &&
                        shouldRemoveActionReference(
                          value.value.reference.source.actionReference,
                          whatActionReferencesRemove,
                          value.value.reference.outputId,
                          outputIdToRemove,
                        )
                      ) {
                        return false;
                      }
                      return true;
                    }
                    return value.value.oneofKind !== "reference";
                  });
                }
                if (item.timeStamp !== undefined) {
                  item.timeStamp.values = item.timeStamp.values
                    .filter((value) => {
                      if (whatActionReferencesRemove) {
                        if (
                          value.value.oneofKind === "reference" &&
                          value.value.reference.source.oneofKind ===
                            "actionReference" &&
                          shouldRemoveActionReference(
                            value.value.reference.source.actionReference,
                            whatActionReferencesRemove,
                            value.value.reference.outputId,
                            outputIdToRemove,
                          )
                        ) {
                          return false;
                        }
                        return true;
                      }

                      return value.value.oneofKind !== "reference";
                    })
                    .map((value) => {
                      if (value.value.oneofKind === "function") {
                        if (
                          value.value.function.function.oneofKind ===
                            "dateAddOffset" &&
                          value.value.function.function.dateAddOffset.value !==
                            undefined
                        ) {
                          value.value.function.function.dateAddOffset.value.values =
                            value.value.function.function.dateAddOffset.value?.values.filter(
                              (value) => {
                                if (whatActionReferencesRemove) {
                                  if (
                                    value.value.oneofKind === "reference" &&
                                    value.value.reference.source.oneofKind ===
                                      "actionReference" &&
                                    shouldRemoveActionReference(
                                      value.value.reference.source
                                        .actionReference,
                                      whatActionReferencesRemove,
                                      value.value.reference.outputId,
                                      outputIdToRemove,
                                    )
                                  ) {
                                    return false;
                                  }
                                  return true;
                                }
                                return value.value.oneofKind !== "reference";
                              },
                            );
                        }
                      }
                      return value;
                    });
                }
                return item;
              }),
            },
          },
        };
      });

      // Remove references(pills) from dateAddOffset value function
      input.value.values = R.map(input.value.values, (value) => {
        if (
          value.value.oneofKind === "function" &&
          value.value.function.function.oneofKind === "dateAddOffset"
        ) {
          if (value.value.function.function.dateAddOffset.value) {
            return {
              value: {
                ...value.value,
                function: {
                  ...value.value.function,
                  function: {
                    ...value.value.function.function,
                    dateAddOffset: {
                      ...value.value.function.function.dateAddOffset,
                      value: {
                        values:
                          value.value.function.function.dateAddOffset.value.values.filter(
                            (value) => {
                              if (whatActionReferencesRemove) {
                                if (
                                  value.value.oneofKind === "reference" &&
                                  value.value.reference.source.oneofKind ===
                                    "actionReference"
                                ) {
                                  return !compareActionReferences(
                                    value.value.reference.source
                                      .actionReference,
                                    whatActionReferencesRemove,
                                  );
                                }
                                return true;
                              }
                              return (
                                value.value.oneofKind !== "reference" ||
                                value.value.reference.source.oneofKind !==
                                  reference
                              );
                            },
                          ),
                      },
                    },
                  },
                },
              },
            };
          }
          return value;
        }
        return value;
      });
    }

    return input;
  }),
});

const shouldRemoveActionReference = (
  actionReference: ActionReference,
  whatActionReferencesRemove?: ActionReference,
  outputId?: string,
  outputIdToRemove?: string,
): boolean => {
  if (
    whatActionReferencesRemove === undefined ||
    !compareActionReferences(actionReference, whatActionReferencesRemove)
  ) {
    return false;
  }

  if (outputIdToRemove === undefined) {
    return true;
  }

  return matchesOutputId(outputId, outputIdToRemove);
};

const matchesOutputId = (
  outputId: string | undefined,
  outputIdToRemove: string,
): boolean => {
  if (outputId === outputIdToRemove) {
    return true;
  }

  if (outputIdToRemove !== CURRENT_ITEM_OUTPUT_ID || outputId === undefined) {
    return false;
  }

  return (
    outputId.startsWith(`${CURRENT_ITEM_OUTPUT_ID}|`) ||
    outputId.startsWith(`${CURRENT_ITEM_OUTPUT_ID}.`)
  );
};
